import React, { useMemo, useState } from 'react';
import { Loader2, Lock, Truck, Store, ChefHat, MapPin, CheckCircle2, Package } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { vendorAdminApi, driverApi, ordersApi } from '../services/api';

/**
 * Ordered status pipeline. Each stage declares:
 *   - `key`          — server-side status string
 *   - `label`        — pretty label
 *   - `role`         — who can flip this toggle (vendor / driver / auto)
 *   - `Icon`         — lucide icon
 * `placed` is auto and cannot be toggled off. Every other stage is manual.
 *
 * Note: when the caller is a "shipper" (courier / logistics / same-day
 * customer), the frontend treats them as the vendor for vendor-owned rows
 * — see the `role === 'shipper'` handling below.
 */
const PIPELINE = [
  { key: 'placed',              label: 'Order placed',        role: 'auto',   Icon: Package },
  { key: 'payment_confirmed',   label: 'Payment confirmed',   role: 'vendor', Icon: CheckCircle2 },
  { key: 'preparing',           label: 'Preparing',           role: 'vendor', Icon: ChefHat },
  { key: 'ready',               label: 'Ready',               role: 'vendor', Icon: Store },
  { key: 'accepted',            label: 'Driver accepted',     role: 'driver', Icon: Truck },
  { key: 'en_route_to_vendor',  label: 'En route to store',   role: 'driver', Icon: Truck },
  { key: 'arrived_at_vendor',   label: 'Arrived at store',    role: 'driver', Icon: MapPin },
  { key: 'picked_up',           label: 'Picked up',           role: 'driver', Icon: Truck },
  { key: 'on_the_way',          label: 'On the way',          role: 'driver', Icon: Truck },
  { key: 'arrived_at_customer', label: 'Arrived at customer', role: 'driver', Icon: MapPin },
  { key: 'delivered',           label: 'Delivered',           role: 'driver', Icon: CheckCircle2 },
];

const _indexByKey = (key) => PIPELINE.findIndex((s) => s.key === key);

const advance = async (role, orderId, status) => {
  if (role === 'vendor') return vendorAdminApi.updateOrderStatus(orderId, status);
  if (role === 'driver') return driverApi.updateOrderStatus(orderId, status);
  if (role === 'shipper') return ordersApi.shipperUpdateStatus(orderId, status);
  throw new Error('Only vendor / driver / shipper can advance');
};

// A "shipper" customer (courier / logistics / same-day) can flip the same
// rows a vendor can. This map tells `handle` which rows the given role owns.
const _rowOwnedBy = (stageRole, callerRole) => {
  if (stageRole === callerRole) return true;
  if (callerRole === 'shipper' && stageRole === 'vendor') return true;
  return false;
};

/**
 * OrderStatusToggles — checklist of switches representing the full order
 * lifecycle. Each toggle:
 *
 *   • Locked (grey) for stages that belong to another role.
 *   • Checked ✓ (green) for stages already reached.
 *   • Tap-to-enable for the NEXT allowed forward step.
 *   • Tap-to-untoggle for the CURRENT stage → reverts to the previous step
 *     (one-step undo — safety net for mis-taps).
 *
 * Props:
 *   • role       — "vendor" | "driver" — who's viewing
 *   • orderId    — for API PATCH
 *   • currentStatus — current status string
 *   • onChanged(newStatus)  — parent refetch hook
 */
const OrderStatusToggles = ({ role, orderId, currentStatus, onChanged, dense = false }) => {
  const [busy, setBusy] = useState(null); // key currently mid-PATCH
  const idxNow = useMemo(() => Math.max(0, _indexByKey(currentStatus || 'placed')), [currentStatus]);

  const nextIdx = idxNow + 1;

  const handle = async (stageIdx, isOn) => {
    const stage = PIPELINE[stageIdx];
    if (!stage) return;
    if (stage.role === 'auto') { toast.error('Order placed is automatic.'); return; }
    if (!_rowOwnedBy(stage.role, role)) {
      toast.error(`Only the ${stage.role} can toggle "${stage.label}".`);
      return;
    }
    let targetStatus;
    if (isOn) {
      if (stageIdx !== nextIdx) {
        toast.error('Please advance one step at a time.');
        return;
      }
      targetStatus = stage.key;
    } else {
      if (stageIdx !== idxNow) {
        toast.error('You can only untoggle the most recent step.');
        return;
      }
      const prev = PIPELINE[stageIdx - 1];
      if (!prev) return;
      targetStatus = prev.key;
    }
    setBusy(stage.key);
    try {
      await advance(role, orderId, targetStatus);
      toast.success(isOn ? `Marked as ${stage.label.toLowerCase()}` : `Reverted to ${PIPELINE[stageIdx - 1]?.label.toLowerCase()}`);
      onChanged?.(targetStatus);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`space-y-1 ${dense ? '' : 'mt-2'}`} data-testid={`order-toggles-${orderId}`}>
      {PIPELINE.map((stage, i) => {
        const isReached = i <= idxNow;
        const isNext = i === nextIdx;
        const owned = _rowOwnedBy(stage.role, role);
        const canFlip = (isReached && i === idxNow && owned) || (isNext && owned);
        const otherRoleLocked = !isReached && !canFlip && stage.role !== 'auto' && !owned;
        const isBusy = busy === stage.key;
        const { Icon } = stage;
        return (
          <div
            key={stage.key}
            className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 ${isReached ? 'bg-emerald-50' : 'bg-slate-50'} ${canFlip ? 'ring-1 ring-emerald-300' : ''}`}
            data-testid={`toggle-row-${orderId}-${stage.key}`}
          >
            <Icon className={`h-4 w-4 shrink-0 ${isReached ? 'text-emerald-600' : 'text-slate-400'}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-medium truncate ${isReached ? 'text-emerald-900' : 'text-slate-700'}`}>
                {stage.label}
              </div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider leading-none">
                {stage.role === 'auto' ? 'Auto' : `${stage.role} controlled`}
                {role === 'shipper' && stage.role === 'vendor' && <span className="ml-1 text-emerald-700">(you)</span>}
                {otherRoleLocked && <span className="ml-1"><Lock className="inline h-2.5 w-2.5" /></span>}
              </div>
            </div>
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
            ) : (
              <Switch
                checked={isReached}
                onCheckedChange={(v) => handle(i, v)}
                disabled={!canFlip || isBusy}
                data-testid={`toggle-${orderId}-${stage.key}`}
                aria-label={`Toggle ${stage.label}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OrderStatusToggles;
export { PIPELINE as ORDER_PIPELINE };
